import { ConnectorDefinition } from 'src/api';
import { connectorVisibleLocalComponents } from './connector-manifest.util';

/** Max age for `last_seen_at` before a registered component is treated as offline. */
export const REALM_COMPONENT_LAST_SEEN_MAX_AGE_MS = 3 * 60 * 1000;

export const CONNECTOR_STATUS_REFRESH_INTERVAL_MS = 20 * 1000;

export type RealmTopologyStatus = 'inactive' | 'pending' | 'active';

export interface RealmAssignmentRow {
  /**
   * New schema: map of component type key → component id
   * (e.g. `{ ramicro_compact: "RAMicro-Client-..." }`). Keys mirror the server
   * component types (`ramicro_compact`, `ramicro_db`, `ramicro_files`, …).
   */
  component_ids?: Record<string, string> | null;
  /** User this assignment belongs to (used to collapse duplicate rows for one user). */
  user_id?: string | null;
  /** Legacy schema fields, kept so old realm_state payloads still assess correctly. */
  compact_component_id?: string | null;
  db_component_id?: string | null;
  files_component_id?: string | null;
  status?: string;
}

/** Component type keys used inside `assignment.component_ids` (see server ComponentType). */
const COMPACT_COMPONENT_KEY = 'ramicro_compact';
const DB_COMPONENT_KEY = 'ramicro_db';
const FILES_COMPONENT_KEY = 'ramicro_files';
const REST_COMPONENT_KEY = 'rest';

/**
 * Reads a component id off an assignment, preferring the new `component_ids` map
 * and falling back to the legacy flat field so both payload shapes are supported.
 */
function assignmentComponentId(
  a: RealmAssignmentRow,
  mapKey: string,
  legacyId: string | null | undefined,
): string | null {
  const map = a.component_ids;
  if (map && typeof map === 'object') {
    const fromMap = map[mapKey];
    if (typeof fromMap === 'string' && fromMap) {
      return fromMap;
    }
  }
  return legacyId ?? null;
}

const compactComponentId = (a: RealmAssignmentRow): string | null =>
  assignmentComponentId(a, COMPACT_COMPONENT_KEY, a.compact_component_id);
const dbComponentId = (a: RealmAssignmentRow): string | null =>
  assignmentComponentId(a, DB_COMPONENT_KEY, a.db_component_id);
const filesComponentId = (a: RealmAssignmentRow): string | null =>
  assignmentComponentId(a, FILES_COMPONENT_KEY, a.files_component_id);
const restComponentId = (a: RealmAssignmentRow): string | null =>
  assignmentComponentId(a, REST_COMPONENT_KEY, null);

export interface RealmConnectivityAssessment {
  topology: RealmTopologyStatus;
  staleRequiredComponentIds: string[];
  /** True when `realm_state.components` has no registered component entries. */
  componentsEmpty: boolean;
  /** True when assignments reference compact/db/files wiring (integration expects components). */
  hasAssignmentWire: boolean;
}

/**
 * Prefer the first instance with `active === true`. Multi-realm installs can list an
 * inactive leftover first; health / import must not hardcode `instances[0]`.
 */
export function connectorActiveInstance(
  connector: ConnectorDefinition,
): NonNullable<ConnectorDefinition['instances']>[number] | undefined {
  return connector.instances?.find((inst) => inst?.active === true);
}

/**
 * Firm/realm instance for reading saved config (active preferred, else inactive).
 * Query responses are already realm-scoped, so `instances[0]` is this firm.
 */
export function connectorRealmInstance(
  connector: ConnectorDefinition,
): NonNullable<ConnectorDefinition['instances']>[number] | undefined {
  return connectorActiveInstance(connector) ?? connector.instances?.[0];
}

export function connectorRealmState(
  connector: ConnectorDefinition,
): Record<string, unknown> | undefined {
  const rs = connectorActiveInstance(connector)?.realm_state;
  if (!rs || typeof rs !== 'object') {
    return undefined;
  }
  return rs as Record<string, unknown>;
}

/** Resolved manifest display name for banners and tooltips (matches connector realm health labels). */
export function connectorIntegrationDisplayLabel(
  connector: ConnectorDefinition,
  lang: string,
): string {
  const manifest = connector.manifest_json;
  const name = manifest?.['name'];
  if (typeof name === 'string') {
    const trimmed = name.trim();
    if (trimmed) {
      return trimmed;
    }
  } else if (typeof name === 'object' && name !== null) {
    const loc = name as Record<string, unknown>;
    const picked = loc[lang] ?? loc['en'];
    if (typeof picked === 'string') {
      const trimmed = picked.trim();
      if (trimmed) {
        return trimmed;
      }
    }
    for (const value of Object.values(loc)) {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) {
          return trimmed;
        }
      }
    }
  }
  return connector.rid || connector.id;
}

/**
 * True when the connector manifest declares on-prem local components (e.g. RA-MICRO installers)
 * that register into `realm_state`. Cloud-only integrations (e.g. Actaport) return false, as do
 * dual-mode ones (DocuWare) while configured for cloud.
 */
export function connectorRequiresRealmTopology(
  connector: ConnectorDefinition,
): boolean {
  return connectorVisibleLocalComponents(
    connector.manifest_json,
    connectorRealmInstance(connector)?.config,
  ).length > 0;
}

export function isComponentAlive(componentEntry: Record<string, unknown> | undefined): boolean {
  if (!componentEntry) {
    return false;
  }
  const last = componentEntry['last_seen_at'];
  if (typeof last !== 'string' || last.length === 0) {
    return false;
  }
  const t = Date.parse(last);
  if (Number.isNaN(t)) {
    return false;
  }
  return Date.now() - t <= REALM_COMPONENT_LAST_SEEN_MAX_AGE_MS;
}

function uniqueIds(ids: (string | null | undefined)[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push(id);
  }
  return out;
}

function parseAssignments(raw: unknown): RealmAssignmentRow[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((a): a is RealmAssignmentRow => a !== null && typeof a === 'object');
}

function asComponentsMap(
  raw: unknown,
): Record<string, Record<string, unknown>> {
  if (!raw || typeof raw !== 'object') {
    return {};
  }
  const out: Record<string, Record<string, unknown>> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (v !== null && typeof v === 'object') {
      out[k] = v as Record<string, unknown>;
    }
  }
  return out;
}

function dedupe(ids: string[]): string[] {
  return [...new Set(ids)];
}

function componentRegisteredAt(
  components: Record<string, Record<string, unknown>>,
  componentId: string,
): string {
  const entry = components[componentId];
  const raw = entry?.['registered_at'];
  return typeof raw === 'string' ? raw : '';
}

/**
 * Among candidate component ids, keep the one whose ``registered_at`` is newest.
 * Prefer ids that exist in ``components``; if none exist, keep a single missing id
 * so topology can still report pending.
 */
function pickLatestComponentId(
  ids: string[],
  components: Record<string, Record<string, unknown>>,
): string | null {
  if (ids.length === 0) {
    return null;
  }
  if (ids.length === 1) {
    return ids[0];
  }

  let bestExisting: string | null = null;
  let bestAt = '';
  for (const id of ids) {
    if (!(id in components)) {
      continue;
    }
    const registeredAt = componentRegisteredAt(components, id);
    if (bestExisting === null || registeredAt > bestAt) {
      bestExisting = id;
      bestAt = registeredAt;
    }
  }
  if (bestExisting) {
    return bestExisting;
  }
  return ids[0];
}

/**
 * Resolve required component ids for a type key from assignments.
 *
 * Duplicate assignment rows for the same user (orphan reinstall) are collapsed by
 * following ``component_ids`` → ``components[id].registered_at`` and keeping the
 * newest mapping. Distinct users still contribute one id each.
 */
function requiredComponentIdsFromAssignments(
  assignments: RealmAssignmentRow[],
  getId: (a: RealmAssignmentRow) => string | null,
  components: Record<string, Record<string, unknown>>,
): string[] {
  const byUser = new Map<string, string[]>();
  assignments.forEach((assignment) => {
    const id = getId(assignment);
    const userId = assignment.user_id;
    // The server always sets user_id; a row without one is malformed and ignored.
    if (!id || typeof userId !== 'string' || !userId) {
      return;
    }
    const list = byUser.get(userId);
    if (list) {
      list.push(id);
    } else {
      byUser.set(userId, [id]);
    }
  });

  const winners: string[] = [];
  for (const ids of byUser.values()) {
    const latest = pickLatestComponentId(uniqueIds(ids), components);
    if (latest) {
      winners.push(latest);
    }
  }
  return uniqueIds(winners);
}

/**
 * Derives Active / Pending / Inactive from realm_state.components and assignments,
 * and lists registered components that are required for the active topology but fail the alive check.
 */
export function assessRealmConnectivity(
  realmState: Record<string, unknown>,
): RealmConnectivityAssessment {
  const components = asComponentsMap(realmState['components']);
  const assignments = parseAssignments(realmState['assignments']);

  const componentKeys = Object.keys(components);
  const componentsEmpty = componentKeys.length === 0;

  const compactIds = requiredComponentIdsFromAssignments(
    assignments,
    compactComponentId,
    components,
  );
  const dbIds = requiredComponentIdsFromAssignments(
    assignments,
    dbComponentId,
    components,
  );
  const filesIds = requiredComponentIdsFromAssignments(
    assignments,
    filesComponentId,
    components,
  );
  const restIds = requiredComponentIdsFromAssignments(
    assignments,
    restComponentId,
    components,
  );
  // Component types that on their own satisfy the topology, unlike the RA-Micro db+files pair.
  const standaloneIds = uniqueIds([...compactIds, ...restIds]);
  const hasStandaloneWire = standaloneIds.length > 0;
  const hasAssignmentWire = hasStandaloneWire || dbIds.length > 0 || filesIds.length > 0;

  const staleRequiredComponentIds: string[] = [];

  const exists = (id: string | null | undefined) => !!id && id in components;

  const recordStaleIfRegistered = (id: string | null | undefined) => {
    if (!exists(id)) {
      return;
    }
    const entry = components[id as string];
    if (!isComponentAlive(entry)) {
      staleRequiredComponentIds.push(id as string);
    }
  };

  if (componentsEmpty) {
    return {
      topology: hasAssignmentWire ? 'pending' : 'inactive',
      staleRequiredComponentIds: [],
      componentsEmpty,
      hasAssignmentWire,
    };
  }

  if (hasStandaloneWire) {
    let pending = false;
    for (const id of standaloneIds) {
      if (!exists(id)) {
        pending = true;
      } else {
        recordStaleIfRegistered(id);
      }
    }
    const stale = dedupe(staleRequiredComponentIds);
    let topology: RealmTopologyStatus = pending ? 'pending' : 'active';
    if (topology === 'active' && stale.length > 0) {
      topology = 'pending';
    }
    return {
      topology,
      staleRequiredComponentIds: stale,
      componentsEmpty,
      hasAssignmentWire,
    };
  }

  if (dbIds.length > 0 && filesIds.length > 0) {
    let pending = false;
    for (const id of [...dbIds, ...filesIds]) {
      if (!exists(id)) {
        pending = true;
      } else {
        recordStaleIfRegistered(id);
      }
    }
    const stale = dedupe(staleRequiredComponentIds);
    let topology: RealmTopologyStatus = pending ? 'pending' : 'active';
    if (topology === 'active' && stale.length > 0) {
      topology = 'pending';
    }
    return {
      topology,
      staleRequiredComponentIds: stale,
      componentsEmpty,
      hasAssignmentWire,
    };
  }

  if (dbIds.length > 0 || filesIds.length > 0) {
    return {
      topology: 'pending',
      staleRequiredComponentIds: [],
      componentsEmpty,
      hasAssignmentWire,
    };
  }

  return {
    topology: 'inactive',
    staleRequiredComponentIds: [],
    componentsEmpty,
    hasAssignmentWire,
  };
}

/** Highest-severity banner bucket wins when multiple integrations are unhealthy (see service). */
export type IntegrationRealmBannerSeverity =
  | 'components_offline'
  | 'topology_incomplete'
  | 'components_empty_waiting_registration'
  | 'no_realm_state'
  | 'realm_inactive';

/** Segments under `pages.settings.connectorSettings.severityHelp.<segment>` for tooltips and help text. */
const SEVERITY_HELP_I18N_SEGMENT: Record<
  IntegrationRealmBannerSeverity,
  string
> = {
  components_offline: 'componentsOffline',
  topology_incomplete: 'topologyIncomplete',
  components_empty_waiting_registration: 'componentsEmptyWaiting',
  no_realm_state: 'noRealmState',
  realm_inactive: 'realmInactive',
};

/** Full ngx-translate key for the explanation / suggested actions of a banner severity. */
export function integrationRealmSeverityHelpTranslationKey(
  severity: IntegrationRealmBannerSeverity,
): string {
  return `pages.settings.connectorSettings.severityHelp.${SEVERITY_HELP_I18N_SEGMENT[severity]}`;
}

/**
 * Classifies realm connectivity for banners and integration detail tooltips.
 *
 * Requires a connector instance row with **`instance.active === true`**. If the integration is toggled off in the UI,
 * we do not classify realm topology (no Pending / offline severity from stale realm_state).
 *
 * {@link ConnectivityHelperService} uses `queryConnectors` with `active: true`, so only enabled integrations are considered there.
 */
export function integrationRealmBannerSeverity(
  connector: ConnectorDefinition,
): IntegrationRealmBannerSeverity | undefined {
  if (!connectorActiveInstance(connector)) {
    return undefined;
  }

  if (!connectorRequiresRealmTopology(connector)) {
    return undefined;
  }

  const rs = connectorRealmState(connector);
  if (!rs) {
    return 'no_realm_state';
  }

  const assessment = assessRealmConnectivity(rs);

  if (assessment.staleRequiredComponentIds.length > 0) {
    return 'components_offline';
  }

  if (assessment.componentsEmpty && assessment.hasAssignmentWire) {
    return 'components_empty_waiting_registration';
  }

  if (assessment.topology === 'pending') {
    return 'topology_incomplete';
  }

  if (assessment.topology === 'inactive') {
    return 'realm_inactive';
  }

  return undefined;
}

/** Row label: Deactive | Pending | Active (requires connector instance). Inactive toggles show Deactive without realm assessment. */
export function connectorIntegrationDisplayStatus(
  connector: ConnectorDefinition,
): 'deactive' | 'pending' | 'active' {
  if (!connectorActiveInstance(connector)) {
    return 'deactive';
  }
  if (!connectorRequiresRealmTopology(connector)) {
    return 'active';
  }
  const rs = connectorRealmState(connector);
  if (!rs) {
    return 'deactive';
  }
  const { topology } = assessRealmConnectivity(rs);
  if (topology === 'inactive') {
    return 'deactive';
  }
  if (topology === 'pending') {
    return 'pending';
  }
  return 'active';
}

/** Shared rule: integration is **on** (active instance) but realm topology is not fully Active (legacy list + new settings dialog). */
export function shouldShowConnectorRealmIssueHelp(
  connector: ConnectorDefinition,
): boolean {
  return (
    !!connectorActiveInstance(connector) &&
    connectorIntegrationDisplayStatus(connector) !== 'active'
  );
}

interface TooltipTranslate {
  instant: (key: string, interpolate?: Record<string, unknown>) => string;
  currentLang?: string;
}

/** Tooltip copy: affected integration line + severity explanation (ngx-translate keys under connector settings + common.integrationIssues). */
export function connectorRealmIssueTooltipText(
  connector: ConnectorDefinition,
  translate: TooltipTranslate,
): string {
  const lang = translate.currentLang || 'en';
  const params = { names: connectorIntegrationDisplayLabel(connector, lang) };
  const affected = translate
    .instant('common.integrationIssues.affectedIntegrations', params)
    .trim();
  const severity = integrationRealmBannerSeverity(connector);
  const explanation = (
    severity
      ? translate.instant(integrationRealmSeverityHelpTranslationKey(severity), params)
      : translate.instant(
          'pages.settings.connectorSettings.severityHelp.notFullyActive',
          params,
        )
  ).trim();
  return [affected, explanation].filter(Boolean).join('\n\n').trim();
}
